import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Moon, Sun, Globe, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [stars, setStars] = useState([]);
  const result = location.state?.result;

  useEffect(() => {
    if (!result) {
      navigate("/");
      return;
    }

    // Generate stars
    const generateStars = () => {
      const newStars = [];
      for (let i = 0; i < 80; i++) {
        newStars.push({
          id: i,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          delay: `${Math.random() * 3}s`
        });
      }
      setStars(newStars);
    };
    generateStars();
  }, [result, navigate]);

  if (!result) return null;

  const planIcons = {
    mental: <Moon className="w-6 h-6" />,
    ame: <Sun className="w-6 h-6" />,
    manifestation: <Globe className="w-6 h-6" />
  };

  const plans = [
    { key: "mental", data: result.mental, emoji: "🌓" },
    { key: "ame", data: result.ame, emoji: "☀️" },
    { key: "manifestation", data: result.manifestation, emoji: "🌍" },
    { key: "cycle_de_vie", data: result.cycle_de_vie, emoji: "🔄" }
  ];

  return (
    <div className="min-h-screen relative pb-12">
      {/* Stars background */}
      <div className="stars-container">
        {stars.map((star) => (
          <div
            key={star.id}
            className="star"
            style={{
              left: star.left,
              top: star.top,
              animationDelay: star.delay
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            data-testid="back-button"
            onClick={() => navigate("/")}
            variant="outline"
            className="border-white/20 bg-white/5 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <img
            src="/elua-logo.png"
            alt="ELUA"
            className="h-16 w-16 object-contain logo-pulse"
          />
        </div>

        {/* Title Section */}
        <div className="text-center mb-12" data-testid="results-header">
          <div className="inline-flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 golden-accent" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 glow-text" style={{ fontFamily: 'Cinzel, serif' }}>
            Votre Lecture Numérologique
          </h1>
          {result.name && (
            <p className="text-2xl text-[#F4C430] font-medium mb-2" data-testid="result-name">
              {result.name}
            </p>
          )}
          <p className="text-xl text-white/80" data-testid="result-date">{result.date}</p>
        </div>

        {/* Results Cards */}
        <div className="space-y-6">
          {plans.map((plan, index) => (
            <Card
              key={plan.key}
              data-testid={`${plan.key}-card`}
              className="glass-card border-2 golden-border card-entrance"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{plan.emoji}</span>
                    <div>
                      <CardTitle className="text-2xl text-white" style={{ fontFamily: 'Cinzel, serif' }}>
                        {plan.data.plan_label}
                      </CardTitle>
                      <p className="text-[#F4C430] text-lg font-semibold mt-1" data-testid={`${plan.key}-frequency`}>
                        Fréquence {plan.data.frequency}
                      </p>
                    </div>
                  </div>
                  <div className="text-white/70">
                    {planIcons[plan.key]}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Aligné */}
                <div data-testid={`${plan.key}-aligned`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-green-400"></div>
                    <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Cinzel, serif' }}>
                      Aligné
                    </h3>
                  </div>
                  <p className="text-white/90 leading-relaxed pl-4">
                    {plan.data.aligned}
                  </p>
                </div>

                <Separator className="bg-white/10" />

                {/* Non-aligné */}
                <div data-testid={`${plan.key}-non-aligned`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                    <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Cinzel, serif' }}>
                      Non-aligné
                    </h3>
                  </div>
                  <p className="text-white/90 leading-relaxed pl-4">
                    {plan.data.non_aligned}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center mt-12">
          <p className="text-white/60 text-sm mb-6">
            Utilisez cette lecture comme guide dans votre cheminement spirituel
          </p>
          <Button
            data-testid="new-reading-button"
            onClick={() => navigate("/")}
            className="bg-gradient-to-r from-[#D4AF37] to-[#F4C430] hover:from-[#F4C430] hover:to-[#D4AF37] text-[#0a2e38] font-semibold px-8 py-6 text-lg"
            style={{ fontFamily: 'Cinzel, serif' }}
          >
            Nouvelle Lecture
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Results;
